import api from '@/services/Api';
import { successSwal } from '@/helper/swals';
import { AxiosResponse } from 'axios';
import { ActionContext, Module } from 'vuex';
import Beleg from '@/types/Beleg';
import Belegposition from '@/types/Belegposition';
import { KassafuehrungState, RootState } from '@/store/types';
import { DataResponse } from '@/types';
import Kassaabschluss from '@/types/Kassaabschluss';
import moment from 'moment';
import { saveAs } from 'file-saver';

export type KassaabschlussPayload = {
    saldo: number;
    anfangssaldo: number;
    gesamtbetrag: string;
    offeneBelege: Beleg[];
    impfstelle_id: number;
    offeneZahlscheinBelege?: Beleg[];
    ziel_tage?: number;
    deletewarteliste?: string | null;

};

export type LoadOffeneBelegeData = DataResponse<{
    belege: Partial<Beleg[]>,
    leistungen: Partial<Belegposition[]>,
}>;

export type LoadKassaabschluesseData = DataResponse<Partial<Kassaabschluss[]>>;

export type LoadKassaabschlusslisteData = DataResponse<{
    abschlussnr: number,
    belege: Partial<Beleg[]>,
    leistungen: Partial<Belegposition[]>,
    abschlussdatum: string,
    anfangssaldo: number,
}>;

type Context = ActionContext<KassafuehrungState, RootState>;

export const kassafuehrung: Module<KassafuehrungState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            kassaliste: false,
            kassaabschluss: false,
            zahlungseingang: false,
            stornieren: false,
        },
        loaders: {
            kassaliste: false,
            offeneBelege: false,
            kassaabschluss: false,
            zahlungseingang: false,
            kassaabschluesse: false,
            kassaabschlussPrint: false,
            kassaabschlussExport: false,
        },
        offeneBelege: [],
        offeneZahlscheinBelege: [],
        beleg: new Beleg(),
        offeneBelegeLeistungen: [],
        kassaabschluesse: [],
        kassaliste: {
            abschlussnr: null,
            belege: [],
            leistungen: [],
            datum: '',
            anfangssaldo: null,
        },
    },
    actions: {
        loadOffeneBelege({ commit, rootState }: Context): void {
            commit('setLoadersOffeneBelege', true);

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('kassa/kassaabschluesse/offenebelege', { impfstelle_id })
                .then((response: AxiosResponse) => commit('setOffeneBelege', response.data))
                .catch(console.error)
                .finally(() => commit('setLoadersOffeneBelege', false));
        },
        loadOffeneZahlscheinBelege({ commit, rootState }: Context, payload?: KassaabschlussPayload): void {
            commit('setLoadersOffeneBelege', true);

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('kassa/kassaabschluesse/offenezahlscheinbelege', { impfstelle_id, zahlungsziel_tage: payload})
                .then((response: AxiosResponse) => commit('setOffeneZahlscheinBelege', response.data))
                .catch(console.error)
                .finally(() => commit('setLoadersOffeneBelege', false));
        },
        loadKassaabschluesse({ commit, rootState }: Context): void {
            commit('setLoadersKassaabschluesse', true);

            const impfstelle_id = rootState.authentication.impfstelle_id;

            api.post('kassa/kassaabschluesse/load', { impfstelle_id })
                .then((response) => commit('setKassaabschluesse', response.data))
                .catch(console.error)
                .finally(() => commit('setLoadersKassaabschluesse', false));
        },
        kassaabschluss({ commit, dispatch }: Context, payload: KassaabschlussPayload): void {
            commit('setLoadersKassaabschluss', true);

            api.post('kassa/kassaabschluesse/kassaabschluss', payload)
                .then(() => {
                    dispatch('loadKassaabschluesse');
                    commit('setDialogKassaabschluss', false);
                    successSwal('Kassaabschluss', 'gebucht');
                })
                .catch(console.error)
                .finally(() => commit('setLoadersKassaabschluss', false));
        },
        loadKassaabschlussliste({ commit }: Context, abschlussnr: number): void {
            commit('setLoadersKassaliste', true);
            commit('setDialogKassaliste', true);

            api.post('kassa/kassaabschluesse/get', { abschlussnr })
                .then((response) => commit('setKassaliste', response.data))
                .catch(console.error)
                .finally(() => commit('setLoadersKassaliste', false));
        },
        printKassaliste({ state, rootState, commit, dispatch }: Context, payload: { abschlussnr?: number }):
            void {
            commit('setLoadersKassaabchlussPrint', true);

            const data = {
                abschlussnr: state.kassaliste.abschlussnr,
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('kassa/kassaabschlussbericht', data, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => dispatch('globals/printDokument', { data }, { root: true }))
                .catch(console.error)
                .finally(() => commit('setLoadersKassaabchlussPrint', false));
        },
        exportKassaliste({ state, rootState, commit }: Context, payload: { abschlussnr?: number }):
            void {
            commit('setLoadersKassaabchlussExport', true);

            const data = {
                abschlussnr: state.kassaliste.abschlussnr,
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('kassa/kassaabschlussbericht', data, { responseType: 'blob' })
                .then(({ data }: AxiosResponse) => {
                    const abschlussnr = data.abschlussnr;
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    saveAs(data, `Kassaabschluss${abschlussnr ? '_' + abschlussnr : ''}_${date}.pdf`);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersKassaabchlussExport', false));
        },
    },
    mutations: {
        setBeleg(state: KassafuehrungState, payload: {beleg: Beleg, load: boolean}): void {
            state.beleg = new Beleg(payload.beleg);
            state.loaders.zahlungseingang = payload.load;
        },
        setOffeneBelege(state: KassafuehrungState, { body }: LoadOffeneBelegeData): void {
            state.offeneBelege = body.belege.map((data) => new Beleg(data));
            state.offeneBelegeLeistungen = body.leistungen.map((data) => new Belegposition(data));
        },
        setOffeneZahlscheinBelege(state: KassafuehrungState, { body }: LoadOffeneBelegeData): void {
            state.offeneZahlscheinBelege = body.belege.map((data) => new Beleg(data));
        },
        setKassaabschluesse(state: KassafuehrungState, { body }: LoadKassaabschluesseData): void {
            state.kassaabschluesse = body.map((data) => new Kassaabschluss(data));
        },
        setKassaliste(state: KassafuehrungState, { body }: LoadKassaabschlusslisteData): void {
            state.kassaliste.datum = body.abschlussdatum;
            state.kassaliste.abschlussnr = body.abschlussnr;
            state.kassaliste.anfangssaldo = body.anfangssaldo;
            state.kassaliste.belege = body.belege.map((data) => new Beleg(data));
            state.kassaliste.leistungen = body.leistungen.map((data) => new Belegposition(data));
        },
        setDialogKassaliste(state: KassafuehrungState, visible: boolean): void {
            state.dialogs.kassaliste = visible;
        },
        setDialogKassaabschluss(state: KassafuehrungState, visible: boolean): void {
            state.dialogs.kassaabschluss = visible;
        },
        setDialogZahlungseingang(state: KassafuehrungState, visible: boolean): void {
            state.dialogs.zahlungseingang = visible;
        },
        setDialogStornieren(state: KassafuehrungState, visible: boolean): void {
            state.dialogs.stornieren = visible;
        },
        setLoadersKassaliste(state: KassafuehrungState, loading: boolean): void {
            state.loaders.kassaliste = loading;
        },
        setLoadersOffeneBelege(state: KassafuehrungState, loading: boolean): void {
            state.loaders.offeneBelege = loading;
        },
        setLoadersKassaabschluesse(state: KassafuehrungState, loading: boolean): void {
            state.loaders.kassaabschluesse = loading;
        },
        setLoadersKassaabschluss(state: KassafuehrungState, loading: boolean): void {
            state.loaders.kassaabschluss = loading;
        },
        setLoadersKassaabchlussPrint(state: KassafuehrungState, loading: boolean): void {
            state.loaders.kassaabschlussPrint = loading;
        },
        setLoadersKassaabchlussExport(state: KassafuehrungState, loading: boolean): void {
            state.loaders.kassaabschlussExport = loading;
        },
    },
};
